import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../lib/theme/index.js';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQuery } from '@tanstack/react-query';
import AuthNavigation from './AuthNavigation';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useUserTypes } from '../hooks/useUserTypes';

export function Navigation() {
  const [showHeader, setShowHeader] = useState(true);
  const [isSmallHeader, setIsSmallHeader] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { currentTheme } = useTheme();
  const location = useLocation();
  const session = useSession();
  const isLoggedIn = Boolean(session?.user);
  const supabase = useSupabaseClient();

  // Get user types from the user_types table
  const { isCampOwner, isParent } = useUserTypes();

  // Only check for owned camps if user is a camp owner
  const { data: ownedCamps = [] } = useQuery(
    ['owned-camps-nav', session?.user?.id],
    async () => {
      if (!session?.user?.id || !isCampOwner) return [];
      
      const { data, error } = await supabase
        .from('camps')
        .select('id, name, slug, claimed, owner_id, plan_id, payment_status')
        .eq('owner_id', session.user.id);
      
      if (error) {
        console.error('Error checking owned camps:', error);
        return [];
      }
      
      return data || [];
    },
    {
      enabled: !!session?.user?.id && isCampOwner,
      staleTime: 30000, // Cache for 30 seconds
      cacheTime: 60000, // Keep in cache for 1 minute
      retry: false
    }
  );

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const isScrollingUp = scrollTop < lastScrollY;
      
      // Track if user has scrolled at all
      setHasScrolled(scrollTop > 0);
      
      if (scrollTop <= 50) {
        // At top: show full header
        setShowHeader(true);
        setIsSmallHeader(false);
      } else if (isScrollingUp) {
        // Scrolling up: show small header
        setShowHeader(true);
        setIsSmallHeader(true);
      } else {
        // Scrolling down: hide header
        setShowHeader(false);
        setIsSmallHeader(false);
      }
      setLastScrollY(scrollTop);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const getLinkClasses = (path) => {
    const isActive = location.pathname === path;
    return `text-primary hover:text-primary/80 px-3 py-2 rounded-xl text-sm font-medium relative transition-all duration-300 ${isActive ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-secondary' : ''}`;
  };

  const getCampsByCityClasses = () => {
    const isActive = location.pathname.startsWith('/camps-by-city');
    return `text-primary hover:text-primary/80 px-3 py-2 rounded-xl text-sm font-medium relative transition-all duration-300 ${isActive ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-secondary' : ''}`;
  };

  const getCampDiariesClasses = () => {
    const isActive = location.pathname.startsWith('/camp-diaries');
    return `text-primary hover:text-primary/80 px-3 py-2 rounded-xl text-sm font-medium relative transition-all duration-300 ${isActive ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-secondary' : ''}`;
  };

  const getForParentsClasses = () => {
    const isActive = location.pathname === '/for-parents';
    return `text-primary hover:text-primary/80 px-3 py-2 rounded-xl text-sm font-medium relative transition-all duration-300 ${isActive ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-secondary' : ''}`;
  };

  const getForCampOwnersClasses = () => {
    const isActive = ['/for-camp-owners', '/pricing/owners', '/mycamp-teaser'].includes(location.pathname);
    return `text-primary hover:text-primary/80 px-3 py-2 rounded-xl text-sm font-medium relative transition-all duration-300 ${isActive ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-secondary' : ''}`;
  };

  const getMobileLinkClasses = (path) => {
    const isActive = location.pathname === path;
    return `block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary transition-colors ${isActive ? 'bg-gray-50 text-primary' : ''}`;
  };

  const getMobileCampsByCityClasses = () => {
    const isActive = location.pathname.startsWith('/camps-by-city');
    return `block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary transition-colors ${isActive ? 'bg-gray-50 text-primary' : ''}`;
  };

  const getMobileCampDiariesClasses = () => {
    const isActive = location.pathname.startsWith('/camp-diaries');
    return `block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary transition-colors ${isActive ? 'bg-gray-50 text-primary' : ''}`;
  };

  const getMobileForParentsClasses = () => {
    const isActive = location.pathname === '/for-parents';
    return `block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary transition-colors ${isActive ? 'bg-gray-50 text-primary' : ''}`;
  };

  const getMobileForCampOwnersClasses = () => {
    const isActive = ['/for-camp-owners', '/pricing/owners', '/mycamp-teaser'].includes(location.pathname);
    return `block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary transition-colors ${isActive ? 'bg-gray-50 text-primary' : ''}`;
  };

  return (
    <nav className={`bg-white/90 backdrop-blur-md border-b border-gray-200 fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${hasScrolled ? 'shadow-lg' : ''} ${showHeader ? (isSmallHeader ? 'h-14' : 'h-20') : 'h-0 -translate-y-full overflow-hidden'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex justify-between items-center h-full">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-start gap-2">
              <img 
                src={currentTheme.logo} 
                alt="CampWhiz" 
                className={`transition-all duration-300 ${isSmallHeader ? 'h-6 lg:h-[30px]' : 'h-12 lg:h-[60px]'}`}
              />
              <span className={`border border-primary text-primary font-semibold rounded-full px-2 transition-all duration-300 ${isSmallHeader ? 'text-[8px] py-0.5' : 'text-[10px] py-0.5'}`}>
                BETA
              </span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link to="/" className={getLinkClasses('/')}>
              Find Camps
            </Link>
            <Link to="/camps-by-city" className={getCampsByCityClasses()}>
              Camps by City
            </Link>
            {!isLoggedIn && (
              <>
                <Link to="/for-parents" className={getForParentsClasses()}>
                  For Parents
                </Link>
                <Link to="/for-camp-owners" className={getForCampOwnersClasses()}>
                  For Camp Owners
                </Link>
              </>
            )}
            <Link to="/camp-diaries" className={getCampDiariesClasses()}>
              Camp Diaries
            </Link>
            <AuthNavigation />
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-primary hover:text-primary/80 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary min-h-[44px] min-w-[44px]"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6 stroke-[2.5]" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6 stroke-[2.5]" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden">
          <div className="fixed inset-0 z-[9998] bg-black bg-opacity-25" onClick={() => setIsMobileMenuOpen(false)} />
          <div 
            className="fixed inset-y-0 right-0 z-[9999] w-full max-w-sm bg-white shadow-xl mobile-menu-panel overflow-y-auto h-screen"
            style={{
              backgroundColor: 'white',
              background: 'white',
              height: '100vh'
            }}
          >
            <div 
              className="flex flex-col h-full"
              style={{
                backgroundColor: 'white',
                background: 'white',
                height: '100%'
              }}
            >
              {/* Mobile menu header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-white">
                <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-primary hover:text-primary/80 hover:bg-gray-100 min-h-[44px] min-w-[44px]"
                >
                  <XMarkIcon className="h-6 w-6 stroke-[2.5]" />
                </button>
              </div>

              {/* Navigation links */}
              <div className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                <Link to="/" className={getMobileLinkClasses('/')} onClick={() => setIsMobileMenuOpen(false)}>
                  Find Camps
                </Link>
                <Link to="/camps-by-city" className={getMobileCampsByCityClasses()} onClick={() => setIsMobileMenuOpen(false)}>
                  Camps by City
                </Link>
                {!isLoggedIn && (
                  <>
                    <Link to="/for-parents" className={getMobileForParentsClasses()} onClick={() => setIsMobileMenuOpen(false)}>
                      For Parents
                    </Link>
                    <Link to="/for-camp-owners" className={getMobileForCampOwnersClasses()} onClick={() => setIsMobileMenuOpen(false)}>
                      For Camp Owners
                    </Link>
                  </>
                )}
                <Link to="/camp-diaries" className={getMobileCampDiariesClasses()} onClick={() => setIsMobileMenuOpen(false)}>
                  Camp Diaries
                </Link>
                
                {/* Auth Navigation - Exposed links with indenting */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <AuthNavigation isMobile={true} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
